import axios from "axios";
import authService from "../services/authService";
import * as Sentry from "@sentry/react";
import { getJdId } from "../utils/decryptURL";
import slotAuthService from "./slotAuthService";
import { multipartAuthFetch, authenticatedFetch } from "./apiInterviewService";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_NEW,
  timeout: 180000, // 3 minutes timeout
  headers: {
    "Content-Type": "application/json",
  },
});

const LOGIN_PATH = "/screenup-login";

// ─── Shared Token Refresh ─────────────────────────────────────────────────────
// One in-flight refresh at a time. Every concurrent 401 awaits the same promise,
// so there is no manual subscriber queue and no duplicate refresh calls.
let refreshPromise = null;

const refreshOnce = () => {
  if (!refreshPromise) {
    refreshPromise = authService
      .refreshAccessToken()
      .then(() => authService.getToken())
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Decode the JWT `exp` claim so we can refresh BEFORE the token expires.
const isTokenExpiringSoon = (token, bufferSec = 60) => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(base64));
    return exp * 1000 - Date.now() < bufferSec * 1000;
  } catch {
    return false; // not a JWT / can't decode → let the 401 path handle it
  }
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Session Expiry Handling ──────────────────────────────────────────────────
// Fires at most once. Clears tokens, remembers where the user was, and notifies
// the app via a window event so React Router can navigate WITHOUT a full page
// reload (see the listener example at the bottom of this file).
let sessionExpiredFired = false;

const handleSessionExpired = () => {
  if (sessionExpiredFired) return;
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;
  sessionExpiredFired = true;

  authService.clearTokens();

  try {
    sessionStorage.setItem(
      "postLoginRedirect",
      window.location.pathname + window.location.search,
    );
  } catch (e) {
    /* storage unavailable – ignore */
  }

  window.dispatchEvent(new CustomEvent("auth:session-expired"));

  // Fallback: if nothing in the app handled the event, do a hard redirect.
  setTimeout(() => {
    if (!window.location.pathname.startsWith(LOGIN_PATH)) {
      window.location.href = LOGIN_PATH;
    }
  }, 300);
};

// Call this after a successful login so future expiries are handled again.
export const resetSessionExpiredFlag = () => {
  sessionExpiredFired = false;
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Request Interceptor ──────────────────────────────────────────────────────
// Attach the current Bearer token to every outgoing request.
//   • If the caller already set its own Authorization header (e.g. the interview
//     token), leave it untouched and mark the request so the 401 handler does
//     not try the HR refresh flow on it.
//   • If the HR token is about to expire, refresh it proactively.

apiClient.interceptors.request.use(
  async (config) => {
    // Preserve explicitly supplied authorization headers.
    if (config.headers?.Authorization) {
      return config;
    }

    try {
      let token = authService.getToken();

      // Do not use isAuthenticated() here because it returns false
      // when the access token has expired.
      if (token && isTokenExpiringSoon(token)) {
        try {
          token = await refreshOnce();
        } catch (refreshError) {
          console.error("Proactive token refresh failed:", refreshError);
          // Let the response interceptor handle a subsequent 401.
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error attaching authentication token:", error);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response Interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // ─────────────────────────────────────────────────────────────
    // Capture ALL non-401 API failures in Sentry
    // ─────────────────────────────────────────────────────────────
    if (status !== 401 || !originalRequest) {
      console.error("API Error:", error.response?.data || error.message);

      Sentry.captureException(error, {
        tags: {
          module: "api-client",
          api_failure: true,
        },

        extra: {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status,
          requestData: originalRequest?.data,
          responseData: error.response?.data,
          params: originalRequest?.params,
        },
      });

      return Promise.reject(error);
    }

    // ─────────────────────────────────────────────────────────────
    // Prevent infinite refresh loop:
    //   • the refresh request itself returned 401, or
    //   • we already retried this request once and it 401'd again
    // ─────────────────────────────────────────────────────────────
    if (originalRequest._isRefreshRequest || originalRequest._retry) {
      Sentry.captureMessage("Session expired after refresh/retry", {
        level: "warning",
        tags: { module: "auth-refresh" },
      });

      handleSessionExpired();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // ─────────────────────────────────────────────────────────────
    // Refresh (shared across all concurrent 401s) and retry once
    // ─────────────────────────────────────────────────────────────
    try {
      const newToken = await refreshOnce();

      // Requests that were sent with the interview token (extractIdData,
      // drop-off, peripheral record, ...) should be retried with the interview
      // token as it stands AFTER the refresh, falling back to the new token.
      const retryToken = originalRequest._usedInterviewToken
        ? sessionStorage.getItem("interviewAuthToken") || newToken
        : newToken;

      originalRequest.headers.Authorization = `Bearer ${retryToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      Sentry.captureException(refreshError, {
        tags: {
          module: "token-refresh",
          auth_failure: true,
        },
        extra: {
          originalUrl: originalRequest?.url,
          originalMethod: originalRequest?.method,
        },
      });

      // Only end the session if the server actually REJECTED the refresh token.
      // Network errors, timeouts and 5xx must not log the user out.
      const refreshStatus =
        refreshError?.response?.status ?? refreshError?.status;
      if (
        refreshStatus === 400 ||
        refreshStatus === 401 ||
        refreshStatus === 403
      ) {
        handleSessionExpired();
      }

      return Promise.reject(refreshError);
    }
  },
);

/*
 * ─── Wiring it up (put this in your top-level component, inside <Router>) ────
 *
 *   const navigate = useNavigate();
 *   useEffect(() => {
 *     const onExpired = () => {
 *       // toast.info("Your session expired. Please log in again.");
 *       navigate("/screenup-login", { replace: true });
 *     };
 *     window.addEventListener("auth:session-expired", onExpired);
 *     return () => window.removeEventListener("auth:session-expired", onExpired);
 *   }, [navigate]);
 *
 * ─── On the login page, after a successful login ─────────────────────────────
 *
 *   import { resetSessionExpiredFlag } from "../services/apiService";
 *
 *   resetSessionExpiredFlag();
 *   const back = sessionStorage.getItem("postLoginRedirect");
 *   sessionStorage.removeItem("postLoginRedirect");
 *   navigate(back || "/dashboard", { replace: true });
 */
// Read a fetch Response body safely (JSON if possible, otherwise null).
const readJsonSafe = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
};

// ─── API Service ──────────────────────────────────────────────────────────────

export const apiService = {
  // Send OTP
  sendOtp: async (userId, phoneNumber) => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const response = await apiClient.post(
        "/PhoneNumberVerification/phone_verify_send-otp",
        {
          id: userId,
          phone_number: phoneNumber,
          company_id: companyId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Send OTP API Error:", error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.details ||
          "Network error. Please check your connection and try again.",
      );
    }
  },
  getCompanyNameAndRole: async (companyId, jdId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_PUBLIC_BASE_URL}/Authentication/public/get/role-company-name/${jdId}/${companyId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Get Company Name and Role Error:", error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.details ||
          "Network error. Please check your connection and try again.",
      );
    }
  },
  // Verify OTP
  verifyOtp: async (userId, phoneNumber, otp) => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const response = await apiClient.post(
        "/PhoneNumberVerification/phone_verify-otp",
        {
          id: userId,
          phone_number: phoneNumber,
          company_id: companyId,
          otp,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Verify OTP API Error:", error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.details ||
          "Invalid OTP. Please try again.",
      );
    }
  },
  // ================= NEW OTP APIs (DO NOT TOUCH OLD ONES) =================

  // Send Email OTP (NEW API)
  sendEmailOtpV2: async (email) => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const jd_id = getJdId();
      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/public/email/send-otp`,
        {
          email,
          company_id: companyId,
          //jd_id
        },
      );
      return response.data;
    } catch (error) {
      console.error("Send Email OTP V2 Error:", error);
      throw new Error(
        error.response?.data?.message || "Failed to send email OTP",
      );
    }
  },

  // Verify Email OTP (NEW API)
  verifyEmailOtpV2: async (email, otp) => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const jdId = sessionStorage.getItem("jdId") || "";

      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/public/email/verify-otp`,
        {
          email,
          email_otp: otp,
          company_id: companyId,
          jd_id: jdId,
        },
      );

      if (response?.data?.screenup_data_collection_template) {
        sessionStorage.setItem(
          "screenup_data_collection_template",
          JSON.stringify(response.data?.screenup_data_collection_template),
        );
      }
      return response.data;
    } catch (error) {
      console.error("Verify Email OTP V2 Error:", error);
      throw new Error(error.response?.data?.message || "Invalid Email OTP");
    }
  },

  // Send Phone OTP (NEW API)
  sendPhoneOtpV2: async (phoneNumber, companyId) => {
    try {
      // react-phone-input-2 returns digits only e.g. "919876543210"
      // API expects E.164 format: "+919876543210"

      const resolvedCompanyId =
        companyId || sessionStorage.getItem("companyId");

      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/public/phone/send-otp`,
        {
          phone_number: phoneNumber,
          company_id: resolvedCompanyId,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Send Phone OTP V2 Error:", error);
      throw new Error(
        error.response?.data?.message || "Failed to send phone OTP",
      );
    }
  },

  // Verify Phone OTP (NEW API)
  verifyPhoneOtpV2: async (phoneNumber, otp, companyId) => {
    try {
      const resolvedCompanyId =
        companyId || sessionStorage.getItem("companyId");

      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/public/phone/verify-otp`,
        {
          phone_number: phoneNumber,
          phone_otp: otp,
          company_id: resolvedCompanyId,
        },
      );

      const data = response.data;

      // ✅ STORE TOKENS
      if (data?.access_token) {
        sessionStorage.setItem("authToken", data.access_token);
        sessionStorage.setItem("interviewAuthToken", data.access_token);
        localStorage.setItem("access_token", data.access_token);
      }

      if (data?.refresh_token) {
        sessionStorage.setItem("refreshToken", data.refresh_token);
        sessionStorage.setItem("interviewRefreshToken", data.refresh_token);
        localStorage.setItem("refresh_token", data.refresh_token);
      }

      if (data?.token_type) {
        sessionStorage.setItem("token_type", data.token_type);
      }

      return data;
    } catch (error) {
      console.error("Verify Phone OTP V2 Error:", error);
      throw new Error(error.response?.data?.message || "Invalid Phone OTP");
    }
  },
  // ── Interview-token endpoints (OCR service) ───────────────────────────────
  // These use the SAME interview token + refresh cycle as
  // set-gaze-benchmark-image (tokenManager in apiInterviewService), so a 401
  // is refreshed via /interview/refresh and retried automatically.
  setInterviewSurvey: async (payload) => {
    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_OCR_BASE_URL}/set-interview-survey/`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(
          data?.message || data?.detail || "Failed to submit survey",
        );
      }
      return data;
    } catch (error) {
      console.error("Error calling set-interview-survey API:", error);
      throw new Error(error.message || "Failed to submit survey");
    }
  },

  setInterviewDropOff: async (interviewId, signal = null) => {
    try {
      const companyId = sessionStorage.getItem("companyId");

      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_OCR_BASE_URL}/set-interview-drop-off/?interview_id=${interviewId}&company_id=${companyId}`,
        {
          method: "POST",
          body: JSON.stringify({}),
          ...(signal ? { signal } : {}),
        },
      );

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || "Drop-off failed");
      }
      return data;
    } catch (error) {
      console.error("Error calling interview drop-off API:", error);
      return null;
    }
  },

  setInterviewPeripheralRecord: async (interviewId, recordData) => {
    try {
      const companyId = sessionStorage.getItem("companyId");

      const payload = {
        interview_id: interviewId,
        record: recordData,
        company_id: companyId,
      };

      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_OCR_BASE_URL}/set-interview-peripheral-record`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );

      const data = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(
          data?.message || data?.detail || "Failed to save peripheral record",
        );
      }
      return data;
    } catch (error) {
      console.error(
        "Error calling set-interview-peripheral-record API:",
        error,
      );
      throw error;
    }
  },

  // Process Screening Interview (data-collection/submit)
  //
  // Accepts a pre-built `answers` array of the form:
  //   [{ question_id, default_id, input: { answer } }]
  //
  // template_id and candidate_id are read from sessionStorage so the
  // caller (Welcome.js handleSubmit) only needs to pass what it knows.
  processScreeningInterview: async (
    resumeId, // maps to candidate_id
    jobDescriptionId,
    answers, // array – built by the caller from sortedFields + formValues
    _sendEmail = false, // kept for backward-compat signature; ignored by new API
    signal = null,
  ) => {
    try {
      const companyId = sessionStorage.getItem("companyId") || "";
      const candidateId = resumeId; // resumeId IS the candidate UUID

      // template_id lives inside the form template object stored by OTP.js
      // under "screenup_form_template" — same place loadNormalizedFieldsFromSession reads it.
      let templateId;
      try {
        const rawTemplate = sessionStorage.getItem("screenup_form_template");
        if (rawTemplate) {
          const parsed = JSON.parse(rawTemplate);
          // Try common key names the backend might use
          templateId = parsed?.id ?? parsed?.template_id ?? parsed?.templateId;
        }
      } catch {
        // leave undefined — API will return a validation error with a clear message
      }

      const requestBody = {
        company_id: companyId,
        template_id: templateId != null ? Number(templateId) : undefined,
        job_description_id: jobDescriptionId,
        candidate_id: candidateId,
        answers,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/data-collection/submit`,
        requestBody,
        {
          signal,
          timeout: 180000,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken") || ""}`,
          },
        },
      );

      // Normalise: callers expect a flat object with the interview/scheduling
      // fields at the top level.  The new API wraps them inside `results`.
      const data = response.data;
      sessionStorage.setItem("interview_id", data.interview_id);
      if (!data?.status) {
        throw new Error(data?.message || "Submission failed");
      }

      // Return results flat so existing redirect logic in Welcome.js keeps working
      return data.results || data;
    } catch (error) {
      console.error("API Error Details:", {
        status: error.response?.status,
        data: error.response?.data,
        requestBody: error.config?.data,
        message: error.message,
      });

      if (error.name === "AbortError" || error.name === "CanceledError") {
        throw new Error("Request was cancelled");
      }

      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.details ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.map((e) => e.message || e).join(", ")
          : error.response?.data?.errors);

      throw new Error(
        backendMessage || "Failed to process screening interview",
      );
    }
  },

  // setMoonshotProctoringStatus: async ({
  //   interviewId,
  //   status,
  //   signal = null,
  // }) => {
  //   try {
  //     const companyId = sessionStorage.getItem("companyId");
  //     const interviewToken = sessionStorage.getItem("interviewAuthToken");

  //     const response = await apiClient.post(
  //       `${process.env.REACT_APP_API_BASE_URL_WITHOUTAPI}/set-moonshot-proctoring-status-mobile/?interview_id=${interviewId}&company_id=${companyId}&status=${status}`,
  //       {},
  //       {
  //         signal,
  //         headers: {
  //           Authorization: `Bearer ${interviewToken}`,
  //           "Content-Type": "application/json",
  //         },
  //       },
  //     );
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error calling set-moonshot-proctoring-status API:", error);
  //     throw new Error(
  //       error.response?.data?.message ||
  //         "Unable to set moonshot proctoring status",
  //     );
  //   }
  // },

  // Extract ID data from captured image
  // Uses the SAME interview token + refresh cycle as set-gaze-benchmark-image
  // (tokenManager access_token/refresh_token in apiInterviewService), so a 401
  // is refreshed via /interview/refresh and retried automatically.
  extractIdData: async (interviewId, imageBlob) => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const formData = new FormData();
      formData.append("company_id", companyId);
      formData.append("interview_id", interviewId);
      formData.append("file", imageBlob, "captured-id.jpg");

      const response = await multipartAuthFetch(
        `${process.env.REACT_APP_API_OCR_BASE_URL}/extract-id`,
        formData,
      );

      const rawText = await response.text();
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        // Non-JSON body – fall back to raw text below.
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.detail ||
            data?.error ||
            rawText ||
            "Failed to extract ID data",
        );
      }

      // Keep the axios-like shape callers already use (response.data / .status)
      return { data, status: response.status };
    } catch (error) {
      throw new Error(error.message || "Failed to extract ID data");
    }
  },

  // ── Public Form Extractor ─────────────────────────────────────────────────
  // POST /public_form/extractor  (multipart/form-data)
  // Submits the candidate's public welcome form including resume file upload.

  // ── Public Form Extractor ────────────────────────────────────────────────
  extractPublicFormData: async (payload) => {
    const { jobDescriptionId, email, templateId, answers, resume } = payload;
    const companyId = sessionStorage.getItem("companyId") || "";
    const url = `${process.env.REACT_APP_API_RESUME_BASE_URL}/public-form-extractor/`;

    const formData = new FormData();
    formData.append("company_id", companyId);
    formData.append("job_description_id", jobDescriptionId || "");
    formData.append("email", email || "");
    formData.append("template_id", templateId || "");
    formData.append("answers", JSON.stringify(answers || []));

    if (resume) {
      formData.append("resume", resume);
    }

    const sendRequest = (token) =>
      axios.post(url, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        timeout: 180000,
      });

    try {
      let response;

      try {
        response = await sendRequest(sessionStorage.getItem("authToken"));
      } catch (error) {
        if (error.response?.status !== 401) {
          throw error;
        }

        // Refresh the ScreenUp access token, sharing any refresh already in progress.
        const newToken = await refreshOnce();

        if (!newToken) {
          throw new Error("Token refresh did not return an access token");
        }

        // Retry the original request only once.
        response = await sendRequest(newToken);
      }

      return response.data;
    } catch (error) {
      console.error("Public Form Extractor API Error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.details;

      throw new Error(
        backendMessage || error.message || "Failed to submit form.",
      );
    }
  },

  // ── Interview Slot Booking ────────────────────────────────────────────────
  // These two endpoints are called from both the HR flow (authService token)
  // and the candidate ScreenUp flow (interview token stored in localStorage
  // by tokenManager after OTP verification). To avoid the wrong token being
  // injected by the apiClient interceptor, both functions make direct fetch
  // calls and resolve the token from localStorage themselves.

  // GET /interview-slot/get-slot-details-by-date/?date=YYYY-MM-DD
  // Returns availability for all 48 half-hour slots on the given date.
  getSlotsByDate: async (dateKey) => {
    try {
      const token =
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("interviewAuthToken") ||
        "";
      const baseUrl = process.env.REACT_APP_API_BASE_URL_NEW;

      const url = `${baseUrl}/interview-slot/get-slot-details-by-date/?date=${encodeURIComponent(dateKey)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.message || err.detail || "Failed to fetch slot availability.",
        );
      }

      return response.json();
    } catch (error) {
      console.error("Get Slots By Date Error:", error);
      throw new Error(error.message || "Failed to fetch slot availability.");
    }
  },

  // POST /interview-slot/book-slot/
  // Books the selected slot for a candidate interview.
  // All params are query params per API spec (slot_id, interview_id, company_id, duration).
  bookSlot: async ({
    slotId,
    interviewId,
    duration,
    isAssessment,
    campaignType,
  }) => {
    const token =
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("interviewAuthToken") ||
      "";

    const companyId = sessionStorage.getItem("companyId");
    const baseUrl = process.env.REACT_APP_API_BASE_URL_NEW;

    const params = new URLSearchParams({
      slot_id: slotId,
      interview_id: interviewId,
      company_id: companyId,
      duration,
      isAssessment,
      campaignType,
    });

    const url = `${baseUrl}/interview-slot/book-slot/?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error("API Error");

        // Preserve original response
        error.status = response.status;
        error.detail = data?.detail;
        error.data = data;
        error.response = data;

        throw error;
      }

      return data;
    } catch (error) {
      console.error("Book Slot Error:", error);
      throw error;
    }
  },

  bookSlotDirect: async ({ slotId, isAssessment, duration }) => {
    try {
      // const resumeId =
      //   sessionStorage.getItem("resumeId") || sessionStorage.getItem("userId");
      const companyId = sessionStorage.getItem("companyId") || "";
      // const jdId = sessionStorage.getItem("jdId");
      const interview_id =
        sessionStorage.getItem("interview_id") ||
        sessionStorage.getItem("interviewId");

      const slotBookfromReset = sessionStorage.getItem("slotBookfromReset");
      const payload = {
        slot_id: slotId,
        company_id: companyId,
        interview_id,
        // job_description_id: jdId,
        // resume_id: resumeId,
        hasExistingAssessment:
          slotBookfromReset === "true" ? true : isAssessment,
        campaignType: "campaign_builder",
        duration,
      };

      const response = await slotAuthService.makeAuthenticatedRequest(
        `${process.env.REACT_APP_API_BASE_URL_NEW}/slot-assign/process`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message || data?.detail || data?.error || "Failed to book slot",
        );
      }

      return data;
    } catch (error) {
      throw new Error(error.message || "Network error occurred");
    }
  },

  getTokenParams: () => {
    return {
      resumeId: authService.getResumeId(),
      jobDescriptionId: authService.getJobDescriptionId(),
      userInfo: authService.getUserInfo(),
    };
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return authService.isAuthenticated();
  },

  // Logout user
  logout: () => {
    authService.clearTokens();
  },
};

export { apiClient };
